import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {SelectedFilter, StockFilter, StockFilterID, StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {StockFilterTypes} from '@Panels/stock-panel/stock-panel.types';
import {getSelectedSubfilterIDForStockFilter} from '@Panels/stock-panel/stock-panel.library';
import {StockFilterPillButton} from '@Panels/stock-panel/components/stock-filter-pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import Divider from '@Components/divider';
import {StockFilterPillDropdown} from '@Panels/stock-panel/components/stock-filter-pill-dropdown';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import styles from './stock-filters.module.scss';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export interface StockFiltersProps {
  selectedFilters: SelectedFilter[];
  items: StockFilter[];
  isVisible?: boolean;
  pillSize: PillButtonSize;
  onFilterItemClicked(filterID: string, subFilterID: null | string): void;
  className?: string;
  addDividers?: boolean;
  isMobile?: boolean;
  selectedTab: StockPanelTabs;
}

export default function StockFilters({isVisible = true, addDividers = true, isMobile = false, ...props}: StockFiltersProps): ReactElement<any> | null {
  const containerRef = useRef<HTMLUListElement>(null);
  const {leftShadow, rightShadow} = useLeftRightScrollShadow(containerRef, true);

  const areFiltersVisible = (): boolean => {
    return isVisible && props.items.length > 0;
  };

  const clickHandler = (filterID: StockFilterID, subFilterID?: string): void => {
    if (props.selectedTab === StockPanelTabs.SHAPES && subFilterID) {
      trackPosterBuilderGA4Events(GA4EventName.MediaModal_ShapeFilter, {[GA4EventParamName.VALUE]: subFilterID});
    }
    props.onFilterItemClicked(filterID, subFilterID ?? null);
  };

  const getMarkupForStockFilter = (stockFilter: StockFilter): ReactElement<any> => {
    return stockFilter.type === StockFilterTypes.BUTTON ? (
      <StockFilterPillButton
        id={stockFilter.id}
        subfilters={stockFilter.subFilters}
        pillSize={props.pillSize}
        selectedSubfilterID={getSelectedSubfilterIDForStockFilter(stockFilter, props.selectedFilters)}
        onSubFilterClicked={clickHandler}
      />
    ) : (
      <StockFilterPillDropdown
        id={stockFilter.id}
        subfilters={stockFilter.subFilters}
        pillSize={props.pillSize}
        selectedSubfilterID={getSelectedSubfilterIDForStockFilter(stockFilter, props.selectedFilters)}
        onSubFilterClicked={clickHandler}
        filterName={stockFilter.name}
        isMobile={isMobile}
        className={!isMobile ? styles.dropDown : undefined}
      />
    );
  };

  return areFiltersVisible() ? (
    <div className={`${styles.filtersContainer} ${rightShadow ? styles.rightShadow : ''} ${leftShadow ? styles.leftShadow : ''} ${props.className ?? ''}`}>
      <ul ref={containerRef} className={`${styles.filtersList} flex-h-row spacing-m-0 _full-height flex-row-align-center`}>
        {props.items.map((stockFilter, index): ReactElement<any> => {
          return (
            <>
              <li key={stockFilter.id} className="spacing-p-0">
                {getMarkupForStockFilter(stockFilter)}
              </li>
              {addDividers && index < props.items.length - 1 ? (
                <li className="spacing-p-0" key={`${stockFilter.id}-divider`}>
                  <Divider width={1} height={props.pillSize === PillButtonSize.MEDIUM ? 40 : 32} />
                </li>
              ) : null}
            </>
          );
        })}
      </ul>
    </div>
  ) : null;
}
